import { COMPANY } from '@/config/company';

// Structured data helpers for SEO
//
// One canonical entity graph for the whole site. The Organization and WebSite
// are declared once (here and mirrored in the static index.html so crawlers
// that do not run JS still see them) and every other node references them by
// @id instead of re-declaring an anonymous Organization.

export const SITE_URL = "https://www.brainfyai.com";
export const ORG_ID = "https://www.brainfyai.com/#organization";
export const WEBSITE_ID = "https://www.brainfyai.com/#website";
export const PERSON_ID = "https://www.brainfyai.com/about#tilak-raj";

export const orgRef = { "@id": ORG_ID };

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  "@id": ORG_ID,
  "name": "Brainfy AI",
  "alternateName": ["Brainfy", "Brainfy AI Inc."],
  "legalName": "Brainfy AI Inc.",
  "url": "https://www.brainfyai.com/",
  "logo": {
    "@type": "ImageObject",
    "url": "https://www.brainfyai.com/favicon-512x512.png",
    "width": 512,
    "height": 512
  },
  "description": "Founder-led BC studio building AI-powered websites, chatbots, CRM automation and workflow automation for Canadian businesses, with fixed-fee packages and no long-term contracts.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": COMPANY.city,
    "addressRegion": COMPANY.regionCode,
    "addressCountry": "CA"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Canada"
  },
  "founder": { "@id": PERSON_ID },
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "support@brainfyai.com",
      "telephone": "+1-780-394-2451",
      "availableLanguage": ["English", "French"],
      "areaServed": "CA"
    }
  ],
  "sameAs": [
    "https://www.linkedin.com/company/brainfyai",
    "https://x.com/Brainfy_AI",
    "https://www.instagram.com/brainfy_ai/",
    "https://www.youtube.com/@BrainfyAI"
  ],
  "knowsAbout": [
    "AI-Powered Websites",
    "AI Chatbots",
    "CRM Automation",
    "Lead Capture",
    "Business Automation",
    "Workflow Automation"
  ]
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  "url": "https://www.brainfyai.com/",
  "name": "Brainfy AI",
  "alternateName": "Brainfy",
  "inLanguage": ["en-CA", "fr-CA"],
  "publisher": orgRef
  // NOTE: no SearchAction — the site has no /search route, so advertising a
  // sitelinks searchbox would point crawlers at a 404. Re-add a SearchAction
  // here only once a real search endpoint exists.
};

// Home page node that points at the single Organization/WebSite. Replaces the
// old standalone ProfessionalService/LocalBusiness block (which duplicated the
// Organization and carried geo/priceRange/hours the site does not show).
export const homePageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.brainfyai.com/#webpage",
  "url": "https://www.brainfyai.com/",
  "isPartOf": { "@id": WEBSITE_ID },
  "about": orgRef
};

// Deprecated alias kept only because the unrouted NewIndex.tsx still imports
// it. It is no longer a LocalBusiness: it resolves to the home WebPage node.
export const localBusinessSchema = homePageSchema;

export const breadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// Schema.org serviceType per /services/<slug>, so each Service node says what it
// actually is instead of one generic label for every page.
export const SERVICE_TYPES: Record<string, string> = {
  'ai-websites': 'Website design and development',
  'ai-chatbots': 'AI chatbot development',
  'crm-automation': 'CRM automation',
  'workflow-automation': 'Business process automation',
  'ai-audit': 'AI strategy audit',
  'ai-voice-agents': 'AI voice agent development',
  'ai-agents': 'AI agent development',
};

export const serviceSchema = (service: {
  name: string;
  description: string;
  url: string;
  price?: string;
  image?: string;
  serviceType?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": service.name,
  "description": service.description,
  "url": service.url,
  "provider": orgRef,
  ...(service.serviceType && { "serviceType": service.serviceType }),
  ...(service.price && { "offers": {
    "@type": "Offer",
    "price": service.price,
    "priceCurrency": "CAD"
  }}),
  ...(service.image && { "image": service.image }),
  "areaServed": {
    "@type": "Country",
    "name": "Canada"
  }
});

export const faqSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const personSchema = (person: {
  name: string;
  jobTitle: string;
  image?: string;
  description?: string;
  sameAs?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": PERSON_ID,
  "name": person.name,
  "url": "https://www.brainfyai.com/about",
  "jobTitle": person.jobTitle,
  ...(person.image && { "image": person.image }),
  ...(person.description && { "description": person.description }),
  ...(person.sameAs && { "sameAs": person.sameAs }),
  "worksFor": orgRef
});
